import { CropAreaPixels, PendingImageAdjustments, PendingImageDimensions } from './pendingUploadTypes';

const SVG_MIME_TYPE = 'image/svg+xml';
const PNG_MIME_TYPE = 'image/png';
const JPEG_MIME_TYPE = 'image/jpeg';
const JPEG_QUALITY_STEPS = [0.92, 0.82, 0.72, 0.62, 0.52, 0.42, 0.32, 0.24];
const IMAGE_SCALE_STEPS = [1, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.15, 0.1, 0.08, 0.06, 0.05];

function createImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image for cropping'));
    image.src = src;
  });
}

export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export async function getImageDimensions(fileOrUrl: File | string): Promise<PendingImageDimensions> {
  const objectUrl = typeof fileOrUrl === 'string' ? fileOrUrl : URL.createObjectURL(fileOrUrl);

  try {
    const image = await createImageElement(objectUrl);

    return {
      width: image.naturalWidth,
      height: image.naturalHeight,
    };
  } finally {
    if (typeof fileOrUrl !== 'string') {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

export function getCenteredCropArea(dimensions: PendingImageDimensions, aspectRatio: number): CropAreaPixels {
  const maxWidth = Math.min(dimensions.width, dimensions.height * aspectRatio);
  const maxHeight = maxWidth / aspectRatio;

  return {
    x: Math.round((dimensions.width - maxWidth) / 2),
    y: Math.round((dimensions.height - maxHeight) / 2),
    width: Math.round(maxWidth),
    height: Math.round(maxHeight),
  };
}

export function getDefaultPendingCropAspectRatio(dimensions: PendingImageDimensions, aspectRatio: number) {
  if (dimensions.width < dimensions.height && aspectRatio > 1) {
    return 1 / aspectRatio;
  }

  return aspectRatio;
}

export function clampCropArea(cropArea: CropAreaPixels, dimensions: PendingImageDimensions): CropAreaPixels {
  const width = clamp(Math.round(cropArea.width), 1, dimensions.width);
  const height = clamp(Math.round(cropArea.height), 1, dimensions.height);

  return {
    width,
    height,
    x: clamp(Math.round(cropArea.x), 0, Math.max(dimensions.width - width, 0)),
    y: clamp(Math.round(cropArea.y), 0, Math.max(dimensions.height - height, 0)),
  };
}

export function getCropAreaForZoom(params: {
  aspectRatio: number;
  cropArea?: CropAreaPixels;
  dimensions: PendingImageDimensions;
  zoom: number;
}): CropAreaPixels {
  const { aspectRatio, cropArea, dimensions, zoom } = params;
  const baseArea = getCenteredCropArea(dimensions, aspectRatio);
  const centerX = cropArea ? cropArea.x + cropArea.width / 2 : dimensions.width / 2;
  const centerY = cropArea ? cropArea.y + cropArea.height / 2 : dimensions.height / 2;
  const width = Math.max(Math.round(baseArea.width / zoom), 1);
  const height = Math.max(Math.round(width / aspectRatio), 1);

  return clampCropArea(
    {
      x: Math.round(centerX - width / 2),
      y: Math.round(centerY - height / 2),
      width,
      height,
    },
    dimensions,
  );
}

export function getCropAreaForAspectRatio(params: {
  aspectRatio: number;
  cropArea?: CropAreaPixels;
  dimensions: PendingImageDimensions;
}) {
  const { aspectRatio, cropArea, dimensions } = params;

  if (!cropArea) {
    return getCenteredCropArea(dimensions, aspectRatio);
  }

  const centerX = cropArea.x + cropArea.width / 2;
  const centerY = cropArea.y + cropArea.height / 2;
  const maxWidthByBounds =
    Math.min(centerX, dimensions.width - centerX, (Math.min(centerY, dimensions.height - centerY) || 0) * aspectRatio) *
    2;
  const requestedWidth = Math.min(cropArea.width, cropArea.height * aspectRatio);
  const width = clamp(Math.round(Math.min(requestedWidth, maxWidthByBounds)), 1, dimensions.width);
  const height = Math.max(Math.round(width / aspectRatio), 1);

  return clampCropArea(
    {
      x: Math.round(centerX - width / 2),
      y: Math.round(centerY - height / 2),
      width,
      height,
    },
    dimensions,
  );
}

export function getContainedSize(params: {
  imageHeight: number;
  imageWidth: number;
  viewportHeight: number;
  viewportWidth: number;
}) {
  const { imageHeight, imageWidth, viewportHeight, viewportWidth } = params;
  const imageAspectRatio = imageWidth / imageHeight;
  const viewportAspectRatio = viewportWidth / viewportHeight;

  if (imageAspectRatio > viewportAspectRatio) {
    const width = viewportWidth;
    const height = width / imageAspectRatio;

    return {
      width,
      height,
      left: 0,
      top: (viewportHeight - height) / 2,
    };
  }

  const height = viewportHeight;
  const width = height * imageAspectRatio;

  return {
    width,
    height,
    left: (viewportWidth - width) / 2,
    top: 0,
  };
}

function getOutputMimeType(file: File, requestedMimeType?: string) {
  if (requestedMimeType) {
    return requestedMimeType;
  }

  if (file.type === PNG_MIME_TYPE || file.type === JPEG_MIME_TYPE) {
    return file.type;
  }

  if (file.type === SVG_MIME_TYPE) {
    return PNG_MIME_TYPE;
  }

  return JPEG_MIME_TYPE;
}

function getFilenameExtension(filename: string) {
  const lastDotIndex = filename.lastIndexOf('.');

  if (lastDotIndex === -1) {
    return '';
  }

  return filename.slice(lastDotIndex + 1);
}

function replaceFilenameExtension(filename: string, extension: string) {
  const lastDotIndex = filename.lastIndexOf('.');

  if (lastDotIndex === -1) {
    return `${filename}.${extension}`;
  }

  return `${filename.slice(0, lastDotIndex)}.${extension}`;
}

function getOutputFilename(file: File, mimeType: string, requestedFilename?: string) {
  const baseFilename = requestedFilename || file.name;
  const currentExtension = getFilenameExtension(baseFilename).toLowerCase();

  if (mimeType === PNG_MIME_TYPE && currentExtension !== 'png') {
    return replaceFilenameExtension(baseFilename, 'png');
  }

  if (mimeType === JPEG_MIME_TYPE && !['jpg', 'jpeg'].includes(currentExtension)) {
    return replaceFilenameExtension(baseFilename, 'jpg');
  }

  return baseFilename;
}

async function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality?: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (nextBlob) => {
        if (!nextBlob) {
          reject(new Error('Failed to export optimized image'));
          return;
        }

        resolve(nextBlob);
      },
      mimeType,
      quality,
    );
  });
}

function drawImageToCanvas(params: {
  canvas: HTMLCanvasElement;
  height: number;
  image: HTMLImageElement;
  width: number;
}) {
  const { canvas, height, image, width } = params;

  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Canvas 2D context is unavailable');
  }

  context.drawImage(image, 0, 0, width, height);

  return context;
}

export function hasPendingImageAdjustments(adjustments: PendingImageAdjustments) {
  return !!(adjustments.brightness || adjustments.contrast || adjustments.warmth);
}

export function getPendingImagePreviewFilter(adjustments: PendingImageAdjustments) {
  const brightness = clamp(100 + adjustments.brightness, 0, 200);
  const contrast = clamp(100 + adjustments.contrast, 0, 200);

  return `brightness(${brightness}%) contrast(${contrast}%)`;
}

export function getPendingImageWarmthOverlay(adjustments: PendingImageAdjustments) {
  return {
    color: adjustments.warmth >= 0 ? 'rgb(255, 153, 64)' : 'rgb(102, 170, 255)',
    opacity: Math.min(Math.abs(adjustments.warmth) / 250, 0.28),
  };
}

export async function normalizeImageFileForUpload(params: { file: File; maxBytes: number; outputFileName?: string }) {
  const { file, maxBytes, outputFileName } = params;

  if (file.size <= maxBytes) {
    return file;
  }

  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await createImageElement(objectUrl);
    const mimeCandidates = file.type === PNG_MIME_TYPE ? [PNG_MIME_TYPE, JPEG_MIME_TYPE] : [JPEG_MIME_TYPE];

    for (const mimeType of mimeCandidates) {
      const qualitySteps = mimeType === JPEG_MIME_TYPE ? JPEG_QUALITY_STEPS : [undefined];

      for (const scale of IMAGE_SCALE_STEPS) {
        const width = Math.max(Math.round(image.naturalWidth * scale), 1);
        const height = Math.max(Math.round(image.naturalHeight * scale), 1);
        const canvas = document.createElement('canvas');

        drawImageToCanvas({
          canvas,
          height,
          image,
          width,
        });

        for (const quality of qualitySteps) {
          const blob = await canvasToBlob(canvas, mimeType, quality);
          const candidateFile = new File([blob], getOutputFilename(file, mimeType, outputFileName), {
            type: mimeType,
            lastModified: Date.now(),
          });

          if (candidateFile.size <= maxBytes) {
            return candidateFile;
          }
        }
      }
    }

    throw new Error(
      `Image is too large to upload. Please use an image under ${Math.round(maxBytes / (1024 * 1024))} MB.`,
    );
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function applyImageAdjustmentsToCanvas(params: {
  adjustments: PendingImageAdjustments;
  context: CanvasRenderingContext2D;
  height: number;
  width: number;
}) {
  const { adjustments, context, height, width } = params;

  if (!hasPendingImageAdjustments(adjustments)) {
    return;
  }

  const sourceImageData = context.getImageData(0, 0, width, height);
  const sourceCanvas = document.createElement('canvas');
  const filteredCanvas = document.createElement('canvas');

  sourceCanvas.width = width;
  sourceCanvas.height = height;
  filteredCanvas.width = width;
  filteredCanvas.height = height;

  const sourceContext = sourceCanvas.getContext('2d');
  const filteredContext = filteredCanvas.getContext('2d');

  if (!sourceContext || !filteredContext) {
    throw new Error('Canvas 2D context is unavailable');
  }

  sourceContext.putImageData(sourceImageData, 0, 0);
  filteredContext.filter = getPendingImagePreviewFilter(adjustments);
  filteredContext.drawImage(sourceCanvas, 0, 0);
  filteredContext.filter = 'none';

  const warmthOverlay = getPendingImageWarmthOverlay(adjustments);

  if (warmthOverlay.opacity > 0) {
    filteredContext.save();
    filteredContext.globalAlpha = warmthOverlay.opacity;
    filteredContext.globalCompositeOperation = 'soft-light';
    filteredContext.fillStyle = warmthOverlay.color;
    filteredContext.fillRect(0, 0, width, height);
    filteredContext.restore();
  }

  const filteredImageData = filteredContext.getImageData(0, 0, width, height);
  context.putImageData(filteredImageData, 0, 0);
}

export async function createCroppedImageFile(params: {
  adjustments?: PendingImageAdjustments;
  croppedAreaPixels: CropAreaPixels;
  file: File;
  outputFileName?: string;
  outputMimeType?: string;
}) {
  const { adjustments, croppedAreaPixels, file, outputFileName, outputMimeType } = params;
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await createImageElement(objectUrl);
    const canvas = document.createElement('canvas');
    const cropArea = clampCropArea(croppedAreaPixels, {
      width: image.naturalWidth,
      height: image.naturalHeight,
    });
    const mimeType = getOutputMimeType(file, outputMimeType);

    canvas.width = cropArea.width;
    canvas.height = cropArea.height;

    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Canvas 2D context is unavailable');
    }

    context.drawImage(
      image,
      cropArea.x,
      cropArea.y,
      cropArea.width,
      cropArea.height,
      0,
      0,
      cropArea.width,
      cropArea.height,
    );

    if (adjustments) {
      applyImageAdjustmentsToCanvas({
        adjustments,
        context,
        height: cropArea.height,
        width: cropArea.width,
      });
    }

    const blob = await canvasToBlob(canvas, mimeType);

    return new File([blob], getOutputFilename(file, mimeType, outputFileName), {
      type: mimeType,
      lastModified: Date.now(),
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
